/* eslint-disable import/no-unresolved,react/jsx-key,jsx-a11y/anchor-has-content,jsx-a11y/anchor-is-valid,jsx-a11y/control-has-associated-label */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation } from 'react-query';
import { SignupComponent } from 'assets/components';
import { useSetting } from '../../../utilities/sharedHooks/settings';
import { getUrlEnvironment, removeWhitespaces } from '../../../utilities/helpers';
import { ACCOUNT_REGIONS } from '../../../utilities/constants';
import { CANADA_COUNTRY_ALPHA, RECAPTCHA_EVENTS, US_COUNTRY_ALPHA } from '../constants';
import {
  validateEmail,
  validateEmailLocalPart,
  validateEmailSpecialChars,
  validatePassword,
} from '../../../utilities/formValidation';
import { showAlert } from '../../../utilities/notifications';
import { FLOWS, sendStats, setMixpanelServer } from '../../../services/mixpanel';
import { EMAIL_VALIDATE_WAITING_LIST_CHECK, EMAIL_VERIFICATION_SEND } from '../mutations';
import {
  MXP_ONBOARDING_SIGN_UP_EMAIL_VERIF_REQUEST,
  MXP_ONBOARDING_SIGN_UP_VISIT,
} from '../mixpanel-events';
import TermsCheckbox from '../../../components/TermsCheckbox';
import { revealPassword } from '../helpers';
import { clearFormError } from '../../../utilities/formsUtils';
import RecaptchaModal from '../../shared/RecaptchaModal';
import { cacheRecaptchaV3failure, clearRecaptchaV3failure } from '../../../utilities/caching';

const Signup = () => {
  const { t: translate } = useTranslation();

  const [accountRegion, setAccountRegion] = useState(ACCOUNT_REGIONS.UK_OR_EU);
  const [showPopUp, setShowPopUp] = useState(false);
  const [showConfirmPasswordPopUp, setShowConfirmPasswordPopUp] = useState(false);
  const [isAllowedToSignup, setIsAllowedToSignup] = useState(true);
  const [showWaitingList, setShowWaitingList] = useState(false);
  const [confirmPasswordRequired, setConfirmPasswordRequired] = useState(false);

  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const eSignTermsRef = useRef(null);
  const [eSignTermsChecked, setESignTermsChecked] = useState(false);
  const [showESignTermsReviewError, setShowESignTermsReviewError] = useState(false);

  const debounceTimerRef = useRef(null);
  const DEBOUNCE_DELAY = 1000;

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    termsChecked: false,
    eSignTermsChecked: false,
    coinmeTermsAndConditions: false,
    countryOfResidenceId: '',
    countryOfResidenceLabel: '',
    countryOfResidenceAlpha: '',
    countryOfResidenceAlpha2: '',
    callingCode: '',
    allowMarketing: true,
    smsOptIn: false,
  });

  const [pswdValidation, setPswdValidation] = useState({
    isLongEnough: false,
    hasNumber: false,
    hasSpecialCharacter: false,
    hasNotAllowerSpecislChars: false,
    hasUppercaseLetter: false,
    hasLowercaseLetter: false,
  });

  const [pswdConfirmValidation, setPswdConfirmValidation] = useState({
    isLongEnough: false,
    hasNumber: false,
    hasSpecialCharacter: false,
    hasNotAllowerSpecislChars: false,
    hasUppercaseLetter: false,
    hasLowercaseLetter: false,
  });

  useEffect(() => {
    sendStats(MXP_ONBOARDING_SIGN_UP_VISIT, {}, FLOWS.ONBOARDING);
  }, []);

  const selectDropDownFields = [
    {
      type: 'img',
      data: 'iso3166alpha2',
      class: 'col-1 text-left position-relative',
      img: {
        sourcePath: './images/flags/1x1/',
        ext: '.svg',
        class: 'rounded-circle position-absolute top-50 start-0 translate-middle-y',
      },
    },
    {
      type: 'text',
      data: 'displayName',
      class: 'col-11 ps-2 text-left',
    },
  ];

  const { path } = useRouteMatch();
  const history = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [allowSeparateEnv] = useSetting('app_features', 'feature_allow_separate_login_flow');
  const [smsConsentPhoneNumber] = useSetting('server_configurations', 'sms_consent_phone_number');
  const [waitingListWebsiteUrl] = useSetting('server_configurations', 'waiting_list_website_url');
  const [waitingListUsWebsiteUrl] = useSetting(
    'server_configurations',
    'waiting_list_website_url_us'
  );
  const [featureAllowLimitAccessForAllowedCountries] = useSetting(
    'server_configurations',
    'limit_access_in_allowed_countries'
  );

  const [specialCharactersAllowed] = useSetting(
    'server_configurations',
    'email_domains_allowed_special_characters'
  );

  const [eSignAgreementUrl] = useSetting('content_urls', 'content_url_esign_agreement');

  const [allowedSignUpCountries] = useSetting('allowed_signup_countries', '', true, false);
  const countryRequiredOptIn =
    form?.countryOfResidenceAlpha === US_COUNTRY_ALPHA ||
    form?.countryOfResidenceAlpha === CANADA_COUNTRY_ALPHA;

  const { mutate: sendEmailVerification, isLoading } = useMutation(EMAIL_VERIFICATION_SEND, {
    onSettled: (data, error) => {
      sendStats(MXP_ONBOARDING_SIGN_UP_EMAIL_VERIF_REQUEST, { error }, FLOWS.ONBOARDING);
      if (!error) {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        history.push(`${path}/confirm-email`, {
          ...form,
          smsOptIn: countryRequiredOptIn ? form?.smsOptIn : null,
          token: data?.token,
          verification_id: data?.verification_id,
        });
      }
    },
    onError: (error) => {
      if (error?.response?.status === 307) {
        setIsOpenRecV2(true);
        cacheRecaptchaV3failure();
      } else {
        clearRecaptchaV3failure();
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const { mutate: checkEmailInWaitingList, isLoading: isLoadingCheckEmailInWaitingList } =
    useMutation(EMAIL_VALIDATE_WAITING_LIST_CHECK, {
      onSettled: (data, error) => {
        if (error) {
          setIsAllowedToSignup(false);
          showAlert({ message: error.message, type: 'danger' });
        } else if (data) {
          if (data?.success && data?.data?.allow_sign_up) {
            setIsAllowedToSignup(true);
          } else {
            setIsAllowedToSignup(false);
          }
        }
      },
    });

  useLayoutEffect(() => {
    setAccountRegion(getUrlEnvironment() || ACCOUNT_REGIONS.UK_OR_EU);
  }, []);

  const isAllowedCountry = useMemo(() => {
    if (allowedSignUpCountries && form?.countryOfResidenceId !== '') {
      const country = allowedSignUpCountries.find((item) => item.id === form.countryOfResidenceId);
      return !!country;
    }
    return true;
  }, [allowedSignUpCountries, form?.countryOfResidenceId]);

  const isEmailAllowed = useMemo(() => {
    if (specialCharactersAllowed !== undefined && form.email) {
      return validateEmailSpecialChars(form.email, specialCharactersAllowed);
    }
    return true;
  }, [specialCharactersAllowed, form]);

  const isEmailLengthAllowed = useMemo(() => {
    if (form.email) {
      const emailParts = form.email.split('@');
      if (emailParts.length < 2) return true;
      return validateEmailLocalPart(form.email);
    }
    return true;
  }, [form.email]);

  const isUSCountry = form?.countryOfResidenceAlpha === US_COUNTRY_ALPHA;

  const disabledButton = useMemo(
    () =>
      !validatePassword(form.password) ||
      (confirmPasswordRequired && form.password?.trim() !== form.confirmPassword?.trim()) ||
      !validateEmail(form.email) ||
      !isEmailLengthAllowed ||
      !form.countryOfResidenceId ||
      !isAllowedCountry ||
      (featureAllowLimitAccessForAllowedCountries && isLoadingCheckEmailInWaitingList) ||
      (featureAllowLimitAccessForAllowedCountries && !isAllowedToSignup) ||
      !isEmailAllowed ||
      (countryRequiredOptIn && !form.smsOptIn) ||
      (isUSCountry && !form.eSignTermsChecked) ||
      (isUSCountry && !form.coinmeTermsAndConditions) ||
      !form.termsChecked,
    [
      form,
      isUSCountry,
      isAllowedCountry,
      isAllowedToSignup,
      featureAllowLimitAccessForAllowedCountries,
      isLoadingCheckEmailInWaitingList,
      confirmPasswordRequired,
      isEmailAllowed,
      isEmailLengthAllowed,
      countryRequiredOptIn,
    ]
  );

  const showWarningMessage = useMemo(
    () => !isAllowedCountry || (featureAllowLimitAccessForAllowedCountries && !isAllowedToSignup),
    [featureAllowLimitAccessForAllowedCountries, isAllowedCountry, isAllowedToSignup]
  );

  const handleSendEmailVerificationRequest = (token) => {
    if (
      !validatePassword(form.password) ||
      !validateEmail(form.email) ||
      !form.termsChecked ||
      (countryRequiredOptIn && !form.smsOptIn)
    ) {
      let errorMsgKey = 'signup_data_error';
      if (!form.termsChecked) {
        errorMsgKey = 'signup_term_condition';
      } else if (countryRequiredOptIn && !form.smsOptIn) {
        errorMsgKey = 'signup_opt_sms';
      }

      showAlert({
        message: translate(errorMsgKey),
        type: 'danger',
      });
      return false;
    }
    if (form) {
      setMixpanelServer(form.countryOfResidenceAlpha2);
      sendEmailVerification({ email: form.email, token, country_id: form.countryOfResidenceId });
    }
    return true;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_EMAIL);
      if (!token) {
        return false;
      }
      handleSendEmailVerificationRequest(token);
    } catch (error) {
      return false;
    }
    return true;
  };

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    handleSendEmailVerificationRequest(tokenV2);
  };

  const handleBlur = (e) => {
    if (e?.target?.name === 'password') {
      setShowPopUp(false);
    }
    if (e?.target?.name === 'confirmPassword') {
      setShowConfirmPasswordPopUp(false);
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (e.target.value) {
      if (e.target.name === 'password') {
        if (!validatePassword(e.target.value, false)) {
          if (e.target.className.indexOf('has-error') === -1) {
            e.target.className += ' has-error';
          }
        } else {
          clearFormError(e);
        }
      } else if (e.target.name === 'confirmPassword') {
        if (e.target.value !== form.password) {
          if (e.target.className.indexOf('has-error') === -1) {
            e.target.className += ' has-error';
          }
        } else {
          clearFormError(e);
        }
      } else if (
        !validateEmail(e.target.value) ||
        !validateEmailSpecialChars(e.target.value, specialCharactersAllowed) ||
        !validateEmailLocalPart(e.target.value)
      ) {
        if (e.target.className.indexOf('has-error') === -1) {
          e.target.className += ' has-error';
        }
      } else if (validateEmail(e.target.value)) {
        if (featureAllowLimitAccessForAllowedCountries) {
          debounceTimerRef.current = setTimeout(async () => {
            checkEmailInWaitingList({ email: e.target.value });
          }, DEBOUNCE_DELAY);
        }
        clearFormError(e);
      } else {
        clearFormError(e);
      }
    }
  };

  const handleChange = (e, fieldName) => {
    if (!e) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (e.displayName) {
      if (fieldName === 'countryOfResidence') {
        setForm({
          ...form,
          countryOfResidenceId: e.id,
          countryOfResidenceLabel: e.displayName,
          countryOfResidenceAlpha: e.iso3166alpha3,
          countryOfResidenceAlpha2: e.iso3166alpha2,
          callingCode: e.callingCode,
        });
      } else {
        setForm({ ...form, [fieldName]: e });
      }
    } else {
      const { name, value } = e.target;
      if (!value) {
        clearFormError(e);
      }
      if (name === 'email') {
        if (validateEmail(value)) {
          if (featureAllowLimitAccessForAllowedCountries) {
            debounceTimerRef.current = setTimeout(async () => {
              checkEmailInWaitingList({ email: e.target.value });
            }, DEBOUNCE_DELAY);
          }
          if (
            validateEmailLocalPart(value) &&
            validateEmailSpecialChars(value, specialCharactersAllowed)
          ) {
            clearFormError(e);
          }
        }
      }
      setForm({ ...form, [name]: value });
      if ((name === 'password' || name === 'confirmPassword') && value) {
        setForm({ ...form, [name]: removeWhitespaces(value) });
        if (name === 'password') {
          setPswdValidation(validatePassword(removeWhitespaces(e.target.value), true));
        }
        if (name === 'confirmPassword') {
          setPswdConfirmValidation(validatePassword(removeWhitespaces(e.target.value), true));
        }
      } else if (e.target.name === 'password') {
        setPswdValidation(validatePassword(removeWhitespaces(e.target.value), true));
      } else if (e.target.name === 'confirmPassword') {
        setPswdConfirmValidation(validatePassword(removeWhitespaces(e.target.value), true));
      } else {
        setShowPopUp(false);
        setShowConfirmPasswordPopUp(false);
      }
    }
  };

  const renderButtons = () => (
    <>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 py-3 text-bold"
          type="submit"
          form="signup-form"
          loading={isLoading ? 'true' : 'false'}
          disabled={disabledButton || isLoading}>
          {translate('create_account_button_label')}
        </button>
        <p className="text-shadow text-bold mt-3">
          {translate('signup_subtitle')} &#x2022;{' '}
          <Link to="/onboarding/login" className="link text-decoration-none">
            {translate('signin_link_label')}
          </Link>
        </p>
      </div>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 py-3 text-bold"
          type="submit"
          form="signup-form"
          loading={isLoading ? 'true' : 'false'}
          disabled={disabledButton || isLoading}>
          {translate('create_account_button_label')}
        </button>
        <p className="text-shadow text-bold mt-3">
          {translate('signup_subtitle')} &#x2022;{' '}
          <Link to="/onboarding/login" className="link text-decoration-none">
            {translate('signin_link_label')}
          </Link>
        </p>
      </div>
    </>
  );

  const renderFooter = (renderWarningMessage) => (
    <>
      {!showWarningMessage ? (
        <div className="d-flex flex-column mt-4 pt-3" style={{ gap: '24px' }}>
          {isUSCountry && (
            <div className="form-check d-flex flex-row align-items-center">
              <div>
                <input
                  type="checkbox"
                  ref={eSignTermsRef}
                  className="form-check-input"
                  checked={form.eSignTermsChecked}
                  onChange={(e) => {
                    if (!eSignTermsChecked) {
                      if (e.target.className.indexOf('has-error') === -1) {
                        e.target.className += ' has-error';
                      }
                      setShowESignTermsReviewError(true);
                    } else {
                      clearFormError(e);
                      setForm({ ...form, eSignTermsChecked: e.target.checked });
                    }
                  }}
                />
              </div>
              <label className="form-check-label letter-spacing-sm">
                <p className="ps-2 text-f6">
                  <Trans
                    i18nKey="us_e_sign_terms_note"
                    components={[
                      <a
                        href="#"
                        className="text-decoration-none"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(eSignAgreementUrl, '_blank', 'noopener,noreferrer');
                          if (eSignTermsRef.current) {
                            eSignTermsRef.current.classList.remove('has-error');
                          }
                          setESignTermsChecked(true);
                          setShowESignTermsReviewError(false);
                        }}
                      />,
                    ]}
                  />
                </p>
                {showESignTermsReviewError && (
                  <p className="text-f6 ps-2  letter-spacing-sm text-danger">
                    {translate('us_e_sign_required_review_note')}
                  </p>
                )}
              </label>
            </div>
          )}
          <div className="form-check d-flex flex-row align-items-center">
            <div>
              <input
                type="checkbox"
                disabled={isUSCountry && !form.eSignTermsChecked}
                className="form-check-input"
                checked={form.allowMarketing}
                onChange={(e) => {
                  setForm({ ...form, allowMarketing: e.target.checked });
                }}
              />
            </div>
            <label
              className="form-check-label letter-spacing-sm"
              style={{ opacity: isUSCountry && !form.eSignTermsChecked ? 0.5 : 1 }}>
              <p className="ps-2 text-f6">{translate('allow_marketing_checkbox_label')} </p>
            </label>
          </div>
          {isUSCountry && (
            <div className="form-check d-flex flex-row align-items-center">
              <div>
                <input
                  type="checkbox"
                  disabled={!form.eSignTermsChecked}
                  className="form-check-input"
                  checked={form.coinmeTermsAndConditions}
                  onChange={(e) => {
                    setForm({ ...form, coinmeTermsAndConditions: e.target.checked });
                  }}
                />
              </div>
              <label
                className="form-check-label letter-spacing-sm"
                style={{ opacity: !form.eSignTermsChecked ? 0.5 : 1 }}>
                <p className="ps-2 text-f6">
                  <Trans
                    i18nKey="coinme_tc_checkbox_label"
                    components={[
                      <a
                        href="https://coinme.com/legal/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-decoration-none"
                      />,
                    ]}
                  />
                </p>
              </label>
            </div>
          )}

          {countryRequiredOptIn && (
            <div className="form-check d-flex flex-row align-items-center">
              <div>
                <input
                  type="checkbox"
                  disabled={isUSCountry && !form.eSignTermsChecked}
                  className="form-check-input"
                  checked={form.smsOptIn}
                  onChange={(e) => {
                    setForm({ ...form, smsOptIn: e.target.checked });
                  }}
                />
              </div>
              <label
                className="form-check-label letter-spacing-sm"
                style={{ opacity: isUSCountry && !form.eSignTermsChecked ? 0.5 : 1 }}>
                <p className="ps-2 text-f6">
                  {isUSCountry
                    ? translate('allow_sms_opt_in_us_note')
                    : translate('allow_sms_opt_in_note', { phoneNumber: smsConsentPhoneNumber })}
                </p>
              </label>
            </div>
          )}

          <div>
            <TermsCheckbox
              contentUrlKey={
                accountRegion === ACCOUNT_REGIONS.US || isUSCountry
                  ? 'content_url_account_creation_us_region'
                  : 'content_url_account_creation'
              }
              disabled={isUSCountry && !form.eSignTermsChecked}
              isUSSignup={isUSCountry}
              checked={form.termsChecked}
              onChange={(e) => {
                setForm({ ...form, termsChecked: e.target.checked });
              }}
            />
          </div>
          {isUSCountry && (
            <div className="d-flex flex-column gap-1">
              <p className="text-f6 text-shadow">{translate('signup_us_patriot_note_title')}</p>
              <p className="text-f6 text-shadow">{translate('signup_us_patriot_note')}</p>
              <p className="text-f6 text-shadow">
                <Trans i18nKey="signup_us_patriot_note_2" components={[<b />]} />
              </p>
            </div>
          )}
        </div>
      ) : (
        renderWarningMessage()
      )}
    </>
  );

  return (
    <>
      <SignupComponent
        accountRegion={accountRegion}
        allowSeparateEnv={allowSeparateEnv}
        showWaitingList={showWaitingList}
        pswdValidation={pswdValidation}
        pswdConfirmValidation={pswdConfirmValidation}
        handleChange={handleChange}
        renderButtons={renderButtons}
        renderFooter={renderFooter}
        isAllowedCountry={isAllowedCountry}
        isAllowedToSignup={isAllowedToSignup}
        isEmailAllowed={isEmailAllowed}
        isEmailLengthAllowed={isEmailLengthAllowed}
        featureAllowLimitAccessForAllowedCountries={featureAllowLimitAccessForAllowedCountries}
        revealPassword={revealPassword}
        showPopUp={showPopUp}
        showConfirmPasswordPopUp={showConfirmPasswordPopUp}
        waitingListWebsiteUrl={isUSCountry ? waitingListUsWebsiteUrl : waitingListWebsiteUrl}
        setShowPopUp={setShowPopUp}
        setShowConfirmPasswordPopUp={setShowConfirmPasswordPopUp}
        handleBlur={handleBlur}
        handleSubmitForm={handleSubmitForm}
        form={form}
        clearFormError={clearFormError}
        setShowWaitingList={setShowWaitingList}
        selectDropDownFields={selectDropDownFields}
        setConfirmPasswordRequired={setConfirmPasswordRequired}
      />
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoading}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default Signup;
